/**
 * Text Input Dialog
 *
 * Reusable modal for simple text-entry actions such as create and rename.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAnimatedMount, ANIMATION_DURATION } from '@/hooks/useAnimatedMount';
import { Button } from './button';
import { Input } from './input';

interface TextInputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
}

export function TextInputDialog({
  isOpen,
  onClose,
  onSubmit,
  title,
  label,
  initialValue = '',
  placeholder,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isLoading = false,
}: TextInputDialogProps) {
  const [value, setValue] = useState(initialValue);
  const { shouldRender, isVisible } = useAnimatedMount(isOpen, {
    exitDuration: ANIMATION_DURATION.normal,
    enterDuration: ANIMATION_DURATION.normal,
  });

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
    }
  }, [initialValue, isOpen]);

  if (!shouldRender) return null;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextValue = value.trim();
    if (!nextValue || isLoading) return;
    onSubmit(nextValue);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className={cn(
          'absolute inset-0 bg-black/50 transition-opacity duration-150',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      <form
        onSubmit={handleSubmit}
        className={cn(
          'relative bg-white dark:bg-surface-900 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden',
          'transition-all duration-150',
          isVisible
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-[0.98] translate-y-1'
        )}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-2">
          <label className="text-sm font-medium text-surface-700 dark:text-surface-300" htmlFor="text-input-dialog-value">
            {label}
          </label>
          <Input
            id="text-input-dialog-value"
            autoFocus
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            disabled={isLoading}
          />
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-surface-50 dark:bg-surface-800">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button type="submit" disabled={!value.trim() || isLoading}>
            {isLoading ? 'Please wait...' : submitLabel}
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}
